import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { format, addDays, subDays, isToday } from 'date-fns'
import { cn } from '@/lib/utils'

interface DateNavigationProps {
  selectedDate: Date
  onDateChange: (newDate: Date) => void
  disabled?: boolean
  className?: string
}

export function DateNavigation({ 
  selectedDate, 
  onDateChange, 
  disabled = false,
  className 
}: DateNavigationProps) {
  // Handle previous date navigation
  const handlePreviousDate = () => {
    if (disabled) return
    onDateChange(subDays(selectedDate, 1))
  }

  // Handle next date navigation
  const handleNextDate = () => {
    if (disabled || isToday(selectedDate)) return
    onDateChange(addDays(selectedDate, 1))
  }

  return (
    <div className={cn("flex items-center justify-center", className)}>
      {/* Previous Date Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={handlePreviousDate}
        disabled={disabled}
        className="flex-shrink-0 h-10 w-10"
        aria-label="Previous day"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Date Display */}
      <div className="mx-2 sm:mx-6 text-center flex-1 min-w-0">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-oswald text-sm uppercase tracking-[1px] text-muted-foreground">
            {isToday(selectedDate) ? 'Today' : 'Date'}
          </span>
        </div>
        <h3 className="font-bebas-neue text-2xl sm:text-3xl tracking-[1px] uppercase">
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </h3>
      </div>

      {/* Next Date Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={handleNextDate}
        disabled={disabled || isToday(selectedDate)}
        className="flex-shrink-0 h-10 w-10"
        aria-label="Next day"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default DateNavigation